import {
  BaseFormGeneratorViewModel,
  FormGeneratorViewProps,
} from "@graffer-inc/form-generator";
import {
  FormError,
  FormSchema,
  FormData as GrafferFormData,
  FormTouched,
} from "@graffer-inc/form-schema";
import {
  FileHolder,
  MyNumberHolder,
  ReadonlyFileRefKeyRegistry,
  ReadonlyMyNumberRefKeyRegistry,
} from "@graffer-inc/cloud-gov-core";

export type ReadOnlyFormGeneratorProps = FormGeneratorViewProps & {
  fileExchanger: ReadonlyFileRefKeyRegistry;
  myNumberExchanger: ReadonlyMyNumberRefKeyRegistry;
  formSubmissionContext: ReadonlyFormViewModel["formSubmissionContext"];
};

export class ReadonlyFormViewModel extends BaseFormGeneratorViewModel<ReadOnlyFormGeneratorProps> {
  constructor(
    public readonly formSchema: FormSchema,
    public values: GrafferFormData,
    public errors: FormError,
    public sectionErrors: FormError[],
    public touched: FormTouched,
    public readonly fileExchanger: ReadonlyFileRefKeyRegistry,
    public readonly myNumberExchanger: ReadonlyMyNumberRefKeyRegistry,
    formSubmissionContext: ReadOnlyFormGeneratorProps["formSubmissionContext"],
  ) {
    super(
      formSchema,
      values,
      errors,
      sectionErrors,
      touched,
      () => ({
        ...this.fileExchanger.getRegistry(),
        ...this.myNumberExchanger.getRegistry(),
      }),
      formSubmissionContext,
    );
  }

  getProps(): ReadOnlyFormGeneratorProps {
    return {
      values: this.values,
      formSchema: this.formSchema,
      errors: this.errors,
      sectionErrors: this.sectionErrors,
      touched: this.touched,
      handleChange: this.handleChange.bind(this),
      handleBlur: this.handleBlur.bind(this),
      handleFocus: this.handleFocus.bind(this),
      setFieldValue: this.setFieldValue.bind(this),
      setFieldTouched: this.setFieldTouched.bind(this),
      setTouched: this.setTouched.bind(this),
      fileExchanger: this.fileExchanger,
      myNumberExchanger: this.myNumberExchanger,
      formSubmissionContext: this.formSubmissionContext,
    };
  }

  static create(args: {
    formSchema: FormSchema;
    initialValues: GrafferFormData;
    validateOnInit?: boolean;
    initialFileRegistry?: FileHolder;
    initialMyNumberRegistry?: MyNumberHolder;
    formSubmissionContext: ReadOnlyFormGeneratorProps["formSubmissionContext"];
  }): ReadonlyFormViewModel {
    const fileReg = new ReadonlyFileRefKeyRegistry(
      args.initialFileRegistry || { files: [] },
    );
    const myNumberReg = new ReadonlyMyNumberRefKeyRegistry(
      args.initialMyNumberRegistry || {},
    );

    const initialSectionErrors = args.formSchema.validateSections(
      args.initialValues,
      {
        ...fileReg.getRegistry(),
        ...myNumberReg.getRegistry(),
      },
      {
        formSubmissionDate: args.formSubmissionContext.submissionDate,
      },
    );

    return new ReadonlyFormViewModel(
      args.formSchema,
      args.initialValues,
      args.validateOnInit
        ? (initialSectionErrors || []).reduce(
            (previousValue, currentValue) => ({
              ...previousValue,
              ...currentValue,
            }),
            {},
          )
        : {},
      args.validateOnInit ? initialSectionErrors : [],
      {},
      fileReg,
      myNumberReg,
      args.formSubmissionContext,
    );
  }
}
